import { useContext } from 'react';
import { FeatureContext, FeatureContextValue } from '../contexts/featureContext';
import { FeatureCode } from '../common/constants/featureCodes';
import { FeatureWithUsage, FeatureCheckResult, FeatureUsageInfo } from '../type/feature-types';

/**
 * Main hook to access all feature context functionality
 */
export const useFeatures = (): FeatureContextValue => {
	const context = useContext(FeatureContext);
	if (!context) {
		throw new Error('useFeatures must be used within a FeatureProvider');
	}
	return context;
};

/**
 * Hook to get a single feature and its status
 */
export const useFeature = (
	code: FeatureCode | string,
): {
	feature: FeatureWithUsage | undefined;
	canUse: boolean;
	checkResult: FeatureCheckResult;
	usage: FeatureUsageInfo | null;
} => {
	const { getFeature, canUse, checkFeature, getUsage } = useFeatures();

	return {
		feature: getFeature(code),
		canUse: canUse(code),
		checkResult: checkFeature(code),
		usage: getUsage(code),
	};
};

/**
 * Simple hook to check if a feature can be used
 */
export const useCanUseFeature = (code: FeatureCode | string): boolean => {
	const { canUse } = useFeatures();
	return canUse(code);
};

/**
 * Hook to get usage info for a limited feature
 */
export const useFeatureUsage = (code: FeatureCode | string): FeatureUsageInfo | null => {
	const { getUsage } = useFeatures();
	return getUsage(code);
};

/**
 * Hook that provides a function to execute an action with feature check
 * Returns a function that:
 * 1. Checks if feature can be used
 * 2. If yes, executes the action and optionally increments usage
 * 3. If no, calls the onBlocked callback
 */
export const useFeatureAction = (
	code: FeatureCode | string,
	options?: {
		trackUsage?: boolean;
		onBlocked?: (result: FeatureCheckResult) => void;
	},
) => {
	const { checkFeature, incrementLocalUsage } = useFeatures();

	const executeWithCheck = async <T>(action: () => Promise<T> | T): Promise<T | null> => {
		const result = checkFeature(code);

		if (!result.allowed) {
			options?.onBlocked?.(result);
			return null;
		}

		const actionResult = await action();

		// Optimistically increment usage on success
		if (options?.trackUsage) {
			incrementLocalUsage(code);
		}

		return actionResult;
	};

	return { executeWithCheck, checkFeature: () => checkFeature(code) };
};
